import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import './SearchSuggestions.scss';

import ProductList from '../common/ProductList';
import Utils from '../utils';
import * as types from '../../store/constants';
import RoutingUtils from '../utils/utils-routing';
import { mapLanguageScopeKeyToCamelCase } from './utils/mappers';

import SearchCatalogueListItem from './SearchCatalogueListItem';
import { TopHit } from './TopHit';
import { useHandleArrowKeySuggestionNavigation } from './useHandleArrowKeySuggestionNavigation';
import { SearchScope } from './utils/types';
import { BundleOut, GenreOut, PerformerOut, ProductSearchResponseOut, TopHitOut, TopHitTag } from '../../store/api';
import { useTranslation } from 'react-i18next';
import { ProductSuggestion } from './ProductSuggestion';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { useEnvironment } from '../components/EnvironmentContext';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import MetadataUtils from '../utils/utils-metadata';
import { useAgeCategorySearchParam } from '../utils/useAgeCategorySearchParam';
import { DISPLAY_TYPE_LIST } from '../common/displayType/types';
import LoadingSpinner from '../components/LoadingSpinner';
import {
    SCOPE_EAUDIOBOOK_OBJECT,
    SCOPE_EBOOK_OBJECT,
    SCOPE_EPRESS_OBJECT,
    SCOPE_LANGUAGELEARNING_OBJECT,
    SCOPE_LOTE_OBJECT
} from './utils/constants';
import {
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EPRESS_OBJECT
} from '../loanFormat/constants';

type Props = {
    readonly searchScope: SearchScope;
    readonly searchSuggestions: { [key: string]: ProductSearchResponseOut };
    readonly searchSuggestionTopHits: ReadonlyArray<TopHitOut>;
    readonly searchQuery: string;
    readonly hideSuggestions: boolean;
    readonly closeSuggestions: () => void;
    readonly onShowAllOfLoanformat: (scopeKey: string) => void;
    readonly onShowAllOfScope: (scope: SearchScope) => void;
    readonly deactivateSearchForm: () => void;
    readonly scrollInnerRef: React.RefObject<HTMLDivElement>;
    readonly onAddPrice: (options: SingleAddOptions) => Promise<void>;
    readonly shopLanguage: string | undefined;
    readonly isSearchSuggestionsFetching: boolean;
};

interface LanguageProductSuggestionsProps {
    searchSuggestions: { [key: string]: ProductSearchResponseOut };
    searchScope: SearchScope;
}

export const SearchSuggestions = ({
    closeSuggestions,
    hideSuggestions,
    onAddPrice,
    onShowAllOfLoanformat,
    onShowAllOfScope,
    scrollInnerRef,
    searchQuery,
    searchScope,
    searchSuggestionTopHits,
    searchSuggestions,
    shopLanguage,
    isSearchSuggestionsFetching
}: Props) => {
    const handleArrowKeySuggestionNavigation = useHandleArrowKeySuggestionNavigation();
    const { t } = useTranslation();
    const env = useEnvironment();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

    const loanFormatUrlKey = mapSearchScopeToLoanFormatUrlKey(searchScope);

    const searchTerms = useMemo(
        () => searchQuery.split(' ').map(term => term.replace(/[^a-zA-Z\d ]/g, '')),
        [searchQuery]
    );

    useEffect(() => {
        if (hideSuggestions) {
            window.removeEventListener('keydown', handleArrowKeySuggestionNavigation);
        }

        window.addEventListener('keydown', handleArrowKeySuggestionNavigation);

        return () => {
            window.removeEventListener('keydown', handleArrowKeySuggestionNavigation);
        };
    }, [handleArrowKeySuggestionNavigation, hideSuggestions]);

    const AuthorTopHit = ({ author }: { author: PerformerOut }) => {
        return (
            <TopHit
                label={t('components.searchResults.refinedSearchCategories.author')}
                photoUrl={MetadataUtils.performerPhotoUrl(author.id, env)}
                onClick={closeSuggestions}
                linkTo={Utils.topHitLocationDescriptor({
                    filter: { author: { include: [author.id] } },
                    scope: searchScope.key,
                    topHitLabel: MetadataUtils.getPerformerFullname(author),
                    isMultiLanguageShopEnabled,
                    shopLanguage,
                    ageCategory: ageCategorySearchParam
                })}
                highlightSearchWords={searchTerms}
                textToHighlight={MetadataUtils.getPerformerFullname(author)!}
            />
        );
    };

    const NarratorTopHit = ({ narrator }: { narrator: PerformerOut }) => {
        return (
            <TopHit
                label={t('components.searchResults.refinedSearchCategories.narrator')}
                photoUrl={MetadataUtils.performerPhotoUrl(narrator.id, env)}
                onClick={closeSuggestions}
                linkTo={Utils.topHitLocationDescriptor({
                    filter: { narrator: { include: [narrator.id] } },
                    scope: searchScope.key,
                    topHitLabel: MetadataUtils.getPerformerFullname(narrator),
                    isMultiLanguageShopEnabled,
                    shopLanguage,
                    ageCategory: ageCategorySearchParam
                })}
                highlightSearchWords={searchTerms}
                textToHighlight={MetadataUtils.getPerformerFullname(narrator)!}
            />
        );
    };

    const SeriesTopHit = ({ seriesName }: { seriesName: string }) => {
        return (
            <TopHit
                label={t('components.searchResults.refinedSearchCategories.series')}
                onClick={closeSuggestions}
                linkTo={Utils.topHitLocationDescriptor({
                    filter: { series: { include: [seriesName] } },
                    scope: searchScope.key,
                    topHitLabel: seriesName,
                    isMultiLanguageShopEnabled,
                    shopLanguage,
                    ageCategory: ageCategorySearchParam
                })}
                highlightSearchWords={searchTerms}
                textToHighlight={seriesName}
            />
        );
    };

    const GenreTopHit = ({ genre }: { genre: GenreOut }) => {
        return (
            <TopHit
                label={t('components.searchResults.refinedSearchCategories.genre')}
                linkTo={
                    loanFormatUrlKey
                        ? RoutingUtils.genrePageLocationDescriptor({
                              loanFormat: loanFormatUrlKey,
                              genreId: genre.id,
                              isMultiLanguageShopEnabled,
                              shopLanguage,
                              ageCategory: ageCategorySearchParam
                          })
                        : Utils.topHitLocationDescriptor({
                              filter: { genre: { include: [genre.id] } },
                              scope: null,
                              topHitLabel: genre.name,
                              shopLanguage,
                              isMultiLanguageShopEnabled,
                              ageCategory: ageCategorySearchParam
                          })
                }
                onClick={closeSuggestions}
                highlightSearchWords={searchTerms}
                textToHighlight={genre.name}
            />
        );
    };

    const BundleTopHit = ({ bundle }: { bundle: BundleOut }) => {
        return (
            <TopHit
                label={t('components.searchSuggestions.curatedCollection')}
                linkTo={`${types.PATH_BUNDLES}/${bundle.bundleId}`}
                onClick={closeSuggestions}
                photoUrl={bundle.coverUrl}
                photoClassName="bundle"
                highlightSearchWords={searchTerms}
                textToHighlight={bundle.title}
            />
        );
    };

    const PublisherImprintTopHit = ({ publisherImprint }: { publisherImprint: string }) => {
        return (
            <TopHit
                label={t('components.searchResults.refinedSearchCategories.publisherImprint')}
                linkTo={Utils.topHitLocationDescriptor({
                    filter: { publisherImprint: { include: [publisherImprint] } },
                    scope: searchScope.key,
                    topHitLabel: publisherImprint,
                    shopLanguage,
                    isMultiLanguageShopEnabled,
                    ageCategory: ageCategorySearchParam
                })}
                onClick={closeSuggestions}
                highlightSearchWords={searchTerms}
                textToHighlight={publisherImprint}
            />
        );
    };

    const PublisherTopHit = ({ publisher }: { publisher: string }) => {
        return (
            <TopHit
                label={t('components.searchResults.refinedSearchCategories.publisher')}
                linkTo={
                    loanFormatUrlKey
                        ? RoutingUtils.publisherPageLocationDescriptor({
                              loanFormat: loanFormatUrlKey,
                              publisher,
                              isMultiLanguageShopEnabled,
                              shopLanguage,
                              ageCategory: ageCategorySearchParam
                          })
                        : Utils.topHitLocationDescriptor({
                              filter: { publisher: { include: [publisher] } },
                              scope: null,
                              topHitLabel: publisher,
                              shopLanguage,
                              isMultiLanguageShopEnabled,
                              ageCategory: ageCategorySearchParam
                          })
                }
                onClick={closeSuggestions}
                highlightSearchWords={searchTerms}
                textToHighlight={publisher}
            />
        );
    };

    const TopHitResult = ({ topHit }: { topHit: TopHitOut }) => {
        switch (topHit.tag) {
            case TopHitTag.AUTHORS:
                return <AuthorTopHit author={topHit.object} />;
            case TopHitTag.NARRATORS:
                return <NarratorTopHit narrator={topHit.object} />;
            case TopHitTag.SERIES_NAME:
                return <SeriesTopHit seriesName={topHit.object} />;
            case TopHitTag.GENRES:
                return <GenreTopHit genre={topHit.object} />;
            case TopHitTag.BUNDLE:
                return <BundleTopHit bundle={topHit.object} />;
            case TopHitTag.PUBLISHER_IMPRINT:
                return <PublisherImprintTopHit publisherImprint={topHit.object} />;
            case TopHitTag.PUBLISHER:
                return <PublisherTopHit publisher={topHit.object} />;
            default:
                return null;
        }
    };

    function LanguageProductSuggestions({ searchSuggestions, searchScope }: LanguageProductSuggestionsProps) {
        const { name: scopeName } = searchScope;
        const camelCaseKey = mapLanguageScopeKeyToCamelCase(searchScope);
        const searchSuggestionHits = searchSuggestions[scopeName]?.hits ?? [];

        return searchSuggestionHits.length > 0 ? (
            <div>
                <h4>
                    {t(`components.searchSuggestions.languageScope.${camelCaseKey}`, {
                        defaultValue: scopeName
                    })}
                    <a className="more" onClick={() => onShowAllOfScope(searchScope)}>
                        {t('components.searchSuggestions.seeAll', {
                            totalHits: searchSuggestions[scopeName].totalHits,
                            label: t(`components.searchSuggestions.languageScope.${camelCaseKey}`, {
                                defaultValue: scopeName
                            })
                        })}
                        <i className="material-icons">keyboard_arrow_right</i>
                    </a>
                </h4>
                <ProductList
                    onAddPrice={onAddPrice}
                    products={searchSuggestionHits}
                    displayType={DISPLAY_TYPE_LIST}
                    isProductSelectable={false}
                    currentPage={0}
                >
                    {({ key, isListView, ...value }) => (
                        <SearchCatalogueListItem
                            {...value}
                            key={key}
                            closeSuggestions={closeSuggestions}
                            shopLanguage={shopLanguage}
                            searchTerms={searchTerms}
                        />
                    )}
                </ProductList>
            </div>
        ) : null;
    }

    if (hideSuggestions) {
        return null;
    }

    const topHitClasses = classNames('top-hit', {
        'no-border': Object.keys(searchSuggestions).length === 0 || searchSuggestionTopHits.length === 0
    });
    const maxHeight = document.body.getBoundingClientRect().height - 140;

    return (
        <>
            {Object.keys(searchSuggestions).length > 0 ||
            searchSuggestionTopHits.length > 0 ||
            isSearchSuggestionsFetching ? (
                <>
                    <div className="search-suggestions-overlay" onClick={closeSuggestions} />
                    <div className="search-suggestions" style={{ maxHeight: `${maxHeight}px` }} ref={scrollInnerRef}>
                        {isSearchSuggestionsFetching ? (
                            <LoadingSpinner width={20} />
                        ) : (
                            <>
                                <div className={topHitClasses}>
                                    {searchSuggestionTopHits.length > 0 ? (
                                        <>
                                            <h4>{t('components.searchSuggestions.topHits')}</h4>
                                            {searchSuggestionTopHits.map(topHit => (
                                                <TopHitResult key={topHit.tag} topHit={topHit} />
                                            ))}
                                        </>
                                    ) : null}
                                </div>
                                <div className="suggestion-product-lists">
                                    <ProductSuggestion
                                        closeSuggestions={closeSuggestions}
                                        onAddPrice={onAddPrice}
                                        searchTerms={searchTerms}
                                        onShowAllOfLoanformat={onShowAllOfLoanformat}
                                        searchSuggestions={searchSuggestions}
                                        loanFormat={LOANFORMAT_EAUDIOBOOK_OBJECT}
                                        scopeKey={SCOPE_EAUDIOBOOK_OBJECT.requestKey}
                                        shopLanguage={shopLanguage}
                                    />
                                    <ProductSuggestion
                                        closeSuggestions={closeSuggestions}
                                        onAddPrice={onAddPrice}
                                        searchTerms={searchTerms}
                                        onShowAllOfLoanformat={onShowAllOfLoanformat}
                                        searchSuggestions={searchSuggestions}
                                        loanFormat={LOANFORMAT_EBOOK_OBJECT}
                                        scopeKey={SCOPE_EBOOK_OBJECT.requestKey}
                                        shopLanguage={shopLanguage}
                                    />
                                    <ProductSuggestion
                                        closeSuggestions={closeSuggestions}
                                        onAddPrice={onAddPrice}
                                        searchTerms={searchTerms}
                                        onShowAllOfLoanformat={onShowAllOfLoanformat}
                                        searchSuggestions={searchSuggestions}
                                        loanFormat={LOANFORMAT_EPRESS_OBJECT}
                                        scopeKey={SCOPE_EPRESS_OBJECT.requestKey}
                                        shopLanguage={shopLanguage}
                                    />
                                    <LanguageProductSuggestions
                                        searchSuggestions={searchSuggestions}
                                        searchScope={SCOPE_LOTE_OBJECT}
                                    />
                                    <LanguageProductSuggestions
                                        searchSuggestions={searchSuggestions}
                                        searchScope={SCOPE_LANGUAGELEARNING_OBJECT}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </>
            ) : null}
        </>
    );
};

function mapSearchScopeToLoanFormatUrlKey(searchScope: SearchScope) {
    switch (searchScope.key) {
        case SCOPE_EAUDIOBOOK_OBJECT.key:
            return LOANFORMAT_EAUDIOBOOK_OBJECT.urlKey;
        case SCOPE_EBOOK_OBJECT.key:
            return LOANFORMAT_EBOOK_OBJECT.urlKey;
        case SCOPE_EPRESS_OBJECT.key:
            return LOANFORMAT_EPRESS_OBJECT.urlKey;
        default:
            return undefined;
    }
}
